import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  MAX_CYCLE_WEEKS,
  MAX_SESSIONS,
  type IntensityGrid,
} from "@jackedlogs/shared";

import { IntensityGridView } from "../IntensityGrid/IntensityGridView";
import { Chip } from "../primitives/Chip";
import { Stepper } from "../primitives/Stepper";
import { useArmedConfirm } from "../primitives/useArmedConfirm";
import { useDisclosure } from "../primitives/useDisclosure";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import type { ComposerStyles } from "./composerStyles";
import type { PlanStyles } from "./planComposerStyles";
import { CloneDayBar } from "./CloneDayBar";
import { CloneWeekBar } from "./CloneWeekBar";
import type { CloneDayResult, CloneWeekUiResult } from "./useComposer";

// The program's day×week navigation spine, rendered above the exercise list
// in the edit-in-place shell (collapsible on narrow, left rail on wide).
// It carries what the day list can't: the at-a-glance intensity grid (the
// whole program's shape), the day selector strip (the always-visible way to
// switch days when the grid is collapsed), and the ⧉ clone-day / clone-week
// composing actions. Selecting a day is also possible by tapping a grid row;
// the strip stays so day switching never needs the grid open.
export function ProgramSpine({
  grid,
  selectedDay,
  onSelectDay,
  dark,
  wide,
  dayCount,
  weekCount,
  hasContent,
  resolveName,
  cloneDay,
  cloneWeek,
  addDay,
  removeDay,
  changeCycleWeeks,
  cs,
  ps,
}: {
  grid: IntensityGrid;
  selectedDay: number;
  onSelectDay: (day: number) => void;
  dark: boolean;
  wide: boolean;
  dayCount: number;
  weekCount: number;
  hasContent: boolean;
  resolveName: (exerciseId: string, snapshot?: string) => string;
  cloneDay: (toDay: number) => CloneDayResult;
  cloneWeek: (fromWeek: number, toWeeks: number[]) => CloneWeekUiResult;
  // PE-11 — structural edits. Add a training day, remove one (guarded at 1),
  // resize the cycle. Absent → the controls hide (create-time / single-plan).
  addDay?: (name: string) => void;
  removeDay?: (idx: number) => void;
  changeCycleWeeks?: (weeks: number) => void;
  cs: ComposerStyles;
  ps: PlanStyles;
}) {
  const { t } = useTranslation();
  // Resolve the full theme locally for the shared <Chip> primitive (the
  // composer threads prebuilt `cs`/`ps` + `dark`, not a UiTheme) — same
  // resolution the composer container does.
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  // Grid is the at-a-glance overview: open by default on wide (the rail has
  // room), collapsed on narrow (the strip drives day switching). CN-15 —
  // swapped the hand-rolled useState+toggle for the shared disclosure
  // primitive (same fold state+a11y machine as the Home weekly-targets card).
  const { expanded: gridOpen, toggle: toggleGrid, a11yProps: gridA11y } =
    useDisclosure(wide);
  // Two-tap confirm on the destructive structural edits (remove-day wipes a
  // whole day; shrinking weeks truncates every slot's later weeks) — the same
  // useArmedConfirm the exercise-remove uses, one level up in severity.
  const [dayConfirm, setDayConfirm] = useArmedConfirm();
  const [weeksConfirm, setWeeksConfirm] = useArmedConfirm();
  // Growing applies immediately; shrinking (data-dropping) arms first.
  const onWeeks = (next: number) => {
    if (next < weekCount && !weeksConfirm) {
      setWeeksConfirm(true);
      return;
    }
    setWeeksConfirm(false);
    changeCycleWeeks?.(next);
  };
  const removeDayLabel = (armed: boolean) =>
    armed
      ? t("composer.removeDayConfirm", {
          n: selectedDay + 1,
          defaultValue: "Confirm — remove day D{{n}}?",
        })
      : t("composer.removeDay", {
          n: selectedDay + 1,
          defaultValue: "Remove day D{{n}}",
        });

  return (
    <View style={{ gap: 10 }}>
      <Pressable
        accessibilityRole="button"
        {...gridA11y}
        onPress={toggleGrid}
        style={ps.disclosureBtn}
      >
        <Text style={ps.disclosureText}>
          {gridOpen ? "▾" : "▸"}{" "}
          {t("composer.gridToggle", { defaultValue: "Intensity grid" })}
        </Text>
      </Pressable>
      {gridOpen ? (
        <IntensityGridView
          grid={grid}
          selected={selectedDay}
          onSelect={onSelectDay}
          dark={dark}
          title={null}
        />
      ) : null}

      {changeCycleWeeks ? (
        <View style={{ gap: 4 }}>
          <Stepper
            theme={theme}
            label={t("composer.weeks", { defaultValue: "Weeks" })}
            value={weekCount}
            min={1}
            max={MAX_CYCLE_WEEKS}
            onChange={onWeeks}
          />
          {weeksConfirm ? (
            <Text
              style={{
                color: theme.palette.danger,
                fontSize: theme.typography.sizes.sm,
              }}
            >
              {t("composer.shrinkWeeksConfirm", {
                n: weekCount,
                defaultValue: "Tap − again to drop week {{n}} and its sets.",
              })}
            </Text>
          ) : null}
        </View>
      ) : null}

      <View style={cs.chipsLine}>
        <Text style={cs.chipsLabel}>
          {t("composer.dayPickLabel", { defaultValue: "Day" })}
        </Text>
        <View style={cs.chipsRow}>
          {Array.from({ length: dayCount }, (_, d) => {
            const on = d === selectedDay;
            return (
              <Chip
                key={d}
                theme={theme}
                shape="box"
                selected={on}
                onPress={() => onSelectDay(d)}
                label={`D${d + 1}`}
                style={{ minWidth: 48 }}
              />
            );
          })}
          {addDay && dayCount < MAX_SESSIONS ? (
            <Chip
              theme={theme}
              shape="box"
              selected={false}
              onPress={() => {
                addDay(`D${dayCount + 1}`);
                onSelectDay(dayCount);
              }}
              label={`＋ ${t("composer.addDay", { defaultValue: "Add day" })}`}
              accessibilityLabel={t("composer.addDay", { defaultValue: "Add day" })}
            />
          ) : null}
        </View>
      </View>

      {removeDay && dayCount > 1 ? (
        <Pressable
          onPress={() => {
            if (!dayConfirm) {
              setDayConfirm(true);
              return;
            }
            setDayConfirm(false);
            removeDay(selectedDay);
            onSelectDay(Math.max(0, selectedDay - 1));
          }}
          accessibilityRole="button"
          accessibilityLabel={removeDayLabel(dayConfirm)}
          hitSlop={8}
          style={[ps.removeBtn, { paddingHorizontal: 12, alignSelf: "flex-start" }]}
        >
          <Text style={ps.removeBtnText}>{removeDayLabel(dayConfirm)}</Text>
        </Pressable>
      ) : null}

      <CloneDayBar
        dayCount={dayCount}
        selectedDay={selectedDay}
        hasContent={hasContent}
        resolveName={resolveName}
        onClone={cloneDay}
        styles={cs}
      />
      <CloneWeekBar
        weekCount={weekCount}
        resolveName={resolveName}
        onClone={cloneWeek}
        styles={cs}
      />
    </View>
  );
}
